/**
 * Issue — where the estimate stops being editable and becomes a commitment.
 *
 * The fork is read from the customer record, not chosen here (FR35). An approval
 * customer reaches an invoice; a walk-in reaches a printout. The estimator is
 * never asked which.
 */

import { useSession } from '../app/SessionProvider.js';
import { buildDocument } from '../document/estimate.js';
import { EstimateSheet } from '../document/EstimateSheet.js';
import type { Job } from '../app/useJob.js';

interface Props {
  job: Job;
  /** Writes the version to the database, then freezes it locally. */
  onIssue: () => void;
  issuing: boolean;
  issueError: string | null;
  customer: { name: string; needsApproval: boolean; terms: string; email: string | null };
  vehicle: string;
  reference: string;
  /**
   * Where the approval link went, once it has gone.
   *
   * Shown with the address, because "sent" without a recipient is exactly the
   * message an estimator cannot act on when the customer says nothing arrived.
   */
  sent?: { sentTo: string; link: string; emailed: boolean } | null;
  /** What the customer wrote when they approved only part of it. */
  customerNote?: string | null;
  /** Send the approval email again — a fresh link, done on purpose. */
  onResend?: () => void;
  resending?: boolean;
  /** An answer that belongs to a version this job has since replaced. */
  staleApproval?: { choice: string; at: string; version: number } | null;
}

export function Issue({ job, customer, vehicle, reference,
                        onIssue, issuing, issueError, sent, customerNote,
                        onResend, resending, staleApproval }: Props) {
  const { t, money, session } = useSession();
  const empty = job.lines.length === 0;

  // Built the same way whether it is a draft on screen or the paper the customer
  // holds. Issuing changes what the document SAYS, not which code drew it.
  const doc = buildDocument({
    reference,
    shopName: session?.shop.name ?? '',
    customer,
    vehicle,
    impact: 'front left impact',
    lines: job.lines,
    totals: job.totals,
    currencyCode: session?.country.currencyCode ?? '',
    estimatorLanguage: session?.language ?? 'en',
    issued: job.issued
      ? { at: job.issued.at, version: job.issued.version, hash: job.issued.hash }
      : null,
  });

  if (!job.issued) {
    return (
      <section className="screen">
        <h2>{t('Issue')}</h2>
        <p className="muted lede">
          Check it, then send. Issuing freezes this version — nothing can change it
          afterwards, which is what makes an approval worth having.
        </p>

        <EstimateSheet doc={doc} />

        {empty && (
          <div className="note warn">
            <b>Nothing on it yet</b>
            Accept something in {t('Review')} first.
          </div>
        )}

        <div className="note">
          <b>This will end at {customer.needsApproval ? 'an invoice' : 'a printout'}</b>
          {customer.name} is set to <b>{customer.needsApproval
            ? t('Needs approval') : t('Auto-approve')}</b>, so it is already decided —
          you are not asked to choose per job.
        </div>

        {issueError && (
          <div className="note warn">
            <b>Not issued</b>
            {issueError} — nothing was frozen, so the estimate is unchanged. Try again.
          </div>
        )}

        {/* The server refuses to email a customer with no address, and it is
            right to. But it refuses AFTER the version has been frozen, and the
            button until now promised an email it could not send. Say it here,
            while the estimator can still do something about it. */}
        {customer.needsApproval && !customer.email && (
          <div className="note warn">
            <b>{customer.name} has no email address</b>
            Nothing can be sent to them. Issuing still works and still locks the
            version — but no approval email will go out, and no link will be
            minted. Add an address on the customer first if you want one.
          </div>
        )}

        <button className="btn primary" disabled={empty || issuing} onClick={onIssue}>
          {issuing ? 'Issuing…'
            : customer.needsApproval && !customer.email
              ? 'Issue — locks this version, no email'
              : 'Issue & email — locks this version'}
        </button>
      </section>
    );
  }

  return (
    <section className="screen">
      <h2>Issued</h2>

      <div className="note good">
        <b>🔒 Locked</b>
        {reference} v{job.issued.version} · {money(job.issued.total)}
        {customer.needsApproval && !sent?.emailed
          ? ' · NOT sent to the customer yet'
          : ' · sent to the customer'}
        . Any change from here creates version {job.issued.version + 1}.
      </div>

      {/* This used to be rendered ONLY before issuing. The one moment it
          matters is the moment after, when the version froze but the email
          failed - and that is exactly when the branch above replaced it, so
          the message was set and then thrown away unseen. */}
      {issueError && (
        <div className="note warn">
          <b>The email did not go out</b>
          {issueError} — the version IS issued and frozen, so this is the mail and
          not the estimate. Fix the customer's address, then send it again below.
        </div>
      )}

      <EstimateSheet doc={doc} />
      <div className="btnrow">
        <button className="btn" onClick={() => window.print()}>Print / save as PDF</button>
      </div>

      {!customer.needsApproval ? (
        <>
          <div className="note">
            <b>Printed — that is the end of it</b>
            {customer.name} pays at the counter and was not asked to answer. They still
            have the estimate by email.
          </div>
          <button className="btn primary" onClick={() => window.print()}>Print</button>
        </>
      ) : !job.answer ? (
        <>
          {/* Loud, because the customer thinks they have answered and the
              shop thinks they have not. Only re-sending closes that gap. */}
          {staleApproval && (
            <div className="note warn">
              <b>They already answered — but on version {staleApproval.version}</b>
              {staleApproval.choice === 'approved' ? 'They approved' : `They answered "${
                staleApproval.choice}" on`} version {staleApproval.version}, and this
              job has since been issued again. That answer does not carry over: they
              agreed to a different document. <b>Send the new one</b> so they can
              answer the estimate you are actually working to.
            </div>
          )}

          <div className="note warn">
            <b>Awaiting the customer</b>
            {sent?.emailed
              ? <>Sent to <b>{sent.sentTo}</b>. Three buttons went out in the email.
                  The job cannot reach an invoice until they answer.</>
              : <>The estimate is issued and frozen. The job cannot reach an invoice
                  until the customer answers.</>}
          </div>

          {/* The link, always. If the mail bounced, was filtered, or the
              customer deleted it, the estimator needs something they can paste
              into a text message rather than a dead end. */}
          {sent?.link && sent.link !== '#' && (
            <div className="note">
              <b>{sent.emailed ? 'Their link' : 'Email did not go out — send this instead'}</b>
              <code className="approvallink">{sent.link}</code>
              <button className="btn small"
                      onClick={() => void navigator.clipboard?.writeText(sent.link)}>
                Copy link
              </button>
            </div>
          )}

          {/* A resend is a deliberate act, not a second click of the same
              button. It mints a NEW token and invalidates nothing — the old
              link still works — so doing it by accident quietly doubles the
              number of live links to one estimate. */}
          {onResend && (
            <p className="muted small">
              Not arrived?{' '}
              <button className="linkish" disabled={resending}
                      onClick={onResend}>
                {resending ? 'Sending…' : 'Send the email again'}
              </button>
              {sent?.sentTo && <> — it went to <b>{sent.sentTo}</b>.</>}
            </p>
          )}

          {/* Customers phone. Recording that answer here keeps the job moving,
              and it is marked as taken by the shop rather than by the customer
              — an approval nobody clicked should never look like one they did. */}
          <p className="muted small">
            If they answer by phone instead, record it here:
          </p>
          <div className="btnrow">
            <button className="btn primary" onClick={() => job.setAnswer('approved')}>
              They approved
            </button>
            <button className="btn" onClick={() => job.setAnswer('partly')}>
              They approved part
            </button>
            <button className="btn" onClick={() => job.setAnswer('declined')}>
              They declined
            </button>
          </div>
        </>
      ) : job.answer === 'approved' ? (
        <>
          <div className="note good">
            <b>✓ Approved</b>
            Recorded against version {job.issued.version}, {money(job.issued.total)},
            with document hash <span className="mono">{job.issued.hash}</span>.
            Work can start.
          </div>
          <div className="doc">
            <div className="doc-h">Invoice — INV-1084</div>
            <div className="doc-m mono">{customer.name} · {customer.terms} · against {reference} v1</div>
            <div className="doc-total"><span>Due</span><span>{money(job.issued.total)}</span></div>
          </div>
        </>
      ) : job.answer === 'partly' ? (
        <>
          <div className="note warn">
            <b>Partly approved — needs you</b>
            {/* Their own words, not a paraphrase and not an example. This is
                the only place the shop learns WHICH part was approved, so it
                is quoted verbatim. */}
            {customerNote
              ? <p>They wrote: <i>“{customerNote}”</i></p>
              : <p className="muted">
                  No note came back with it — ring them before revising, because
                  nothing here says which part they meant.
                </p>}
            <p>Nothing is auto-adjusted. Revise and re-send — that becomes version 2,
              and it needs its own approval before an invoice can exist.</p>
          </div>
          <button className="btn primary" onClick={job.revise}>Revise → version 2</button>
        </>
      ) : (
        <>
          <div className="note bad">
            <b>Declined</b>
            {customerNote
              ? <p>They wrote: <i>“{customerNote}”</i></p>
              : <p className="muted">No note came back with it.</p>}
            <p>The estimate and the refusal stay on file either way — the record you want
              if they come back in three months. If this was really "not like this" rather
              than "not at all", revise and re-send: that becomes version 2, and it needs
              its own approval before an invoice can exist.</p>
          </div>
          <button className="btn primary" onClick={job.revise}>Revise → version 2</button>
        </>
      )}
    </section>
  );
}

